import { useMemo } from 'react'

import { Chip, DescriptionList, SkeletonBox } from '@circlefin/components'
import { useDate, useMoney } from '@circlefin/hooks'
import {
  CustomerFxBatchSettlementDetailStatus,
  SettlementDetailType,
  type CustomerFxBatchSettlementDetail,
} from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

export interface CustomerFxBatchSettlementDetailCardProps {
  /**
   * Title for card.
   */
  title: string
  /**
   * Customer FX batch settlement detail.
   */
  settlementDetail?: CustomerFxBatchSettlementDetail
  /**
   * Display loading state.
   */
  loading?: boolean
}

export const CustomerFxBatchSettlementDetailCard: React.FC<
  CustomerFxBatchSettlementDetailCardProps
> = ({ title, settlementDetail, loading }) => {
  const { t } = useTranslation('payments')
  const { money } = useMoney()
  const { date: formatDate } = useDate()
  const statusVariant = useMemo(() => {
    switch (settlementDetail?.status) {
      case CustomerFxBatchSettlementDetailStatus.completed:
        return 'status/success'
      case CustomerFxBatchSettlementDetailStatus.pending:
      case undefined:
        return 'status/neutral'
    }
  }, [settlementDetail?.status])

  if (loading) {
    return (
      <div className="space-y-3">
        <SkeletonBox className="h-4 w-88" loading={loading} />
        <SkeletonBox className="h-4 w-120" loading={loading} />
        <SkeletonBox className="h-4 w-48" loading={loading} />
      </div>
    )
  }

  if (!settlementDetail) {
    return null
  }

  return (
    <>
      <h2 className="type-h-title-md">{title}</h2>
      <DescriptionList density="loose">
        <DescriptionList.Label className="w-64">
          {t('customerFxBatchSettlementDetailCard.settlementDetailId')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{settlementDetail.id}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('customerFxBatchSettlementDetailCard.type')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="capitalize type-body-base-bold">
            {settlementDetail.type}
          </p>
        </DescriptionList.Description>

        <DescriptionList.Label className="w-64">
          {t('customerFxBatchSettlementDetailCard.reference')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">{settlementDetail.reference}</p>
        </DescriptionList.Description>

        <DescriptionList.Label className="h-full w-64">
          {t('customerFxBatchSettlementDetailCard.status')}
        </DescriptionList.Label>
        <DescriptionList.Description className="flex flex-col">
          <Chip variant={statusVariant}>
            <p className="capitalize type-body-base-bold">
              {settlementDetail.status}
            </p>
          </Chip>
          <p className="type-body-xs">
            {formatDate(settlementDetail.updateDate, 'dateTimeWithYear')}
          </p>
        </DescriptionList.Description>
        <DescriptionList.Label className="w-64">
          {t('customerFxBatchSettlementDetailCard.amount')}
        </DescriptionList.Label>
        <DescriptionList.Description>
          <p className="type-body-base-bold">
            {money({
              number: settlementDetail.amount.amount,
              variant: settlementDetail.amount.currency,
            })}
          </p>
        </DescriptionList.Description>

        {settlementDetail.type === SettlementDetailType.payable && (
          <>
            <DescriptionList.Label className="w-64">
              {t('customerFxBatchSettlementDetailCard.expectedPaymentDueAt')}
            </DescriptionList.Label>
            <DescriptionList.Description>
              <p className="type-body-base-bold">
                {formatDate(
                  settlementDetail.expectedPaymentDueAt ?? '',
                  'dateTimeWithYear',
                )}
              </p>
            </DescriptionList.Description>
          </>
        )}
      </DescriptionList>
    </>
  )
}
